import { useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import {
  getGetJobApiV1SendMassJobsJobIdGetQueryKey,
  useGetJobApiV1SendMassJobsJobIdGet,
} from '#/shared/api/generated/communication/theCargoCommunication'
import type {
  getJobApiV1SendMassJobsJobIdGet,
  listJobsApiV1SendMassJobsGet,
} from '#/shared/api/generated/communication/theCargoCommunication'
import {
  MassJobStatus,
  MassCallPhase,
} from '#/shared/api/generated/communication/models'
import type {
  MassJobSummary,
  MassCallLiveState,
} from '#/shared/api/generated/communication/models'
import { activeMassJobsQueryKey } from '#/shared/lib/mass-jobs'
import { WS_MESSAGE_TYPE, useWsSubscriptions } from '#/shared/lib/ws'
import type { WsEnvelope } from '#/shared/lib/ws'

type JobDetailResponse = Awaited<
  ReturnType<typeof getJobApiV1SendMassJobsJobIdGet>
>
type MassJobsResponse = Awaited<ReturnType<typeof listJobsApiV1SendMassJobsGet>>

/** `GET /send/mass/jobs/{id}` answers with the send report for sms/email jobs
 *  and the live dial state for call jobs — pick out the latter. */
function asLiveState(
  detail: JobDetailResponse['data'] | undefined,
): MassCallLiveState | null {
  return detail && 'session_id' in detail ? detail : null
}

const PHASE_STATUS: Record<MassCallPhase, MassJobStatus> = {
  [MassCallPhase.initializing]: MassJobStatus.queued,
  [MassCallPhase.dialing]: MassJobStatus.processing,
  [MassCallPhase.on_call]: MassJobStatus.processing,
  [MassCallPhase.paused]: MassJobStatus.paused,
  [MassCallPhase.completed]: MassJobStatus.completed,
  [MassCallPhase.cancelled]: MassJobStatus.cancelled,
}

/** Fold a live-state frame into the cached jobs-list summary so the collapsed
 *  pill's numbers track the socket without a refetch. */
function patchSummary(
  items: MassJobSummary[],
  state: MassCallLiveState,
): MassJobSummary[] | null {
  const index = items.findIndex((job) => job.id === state.session_id)
  if (index === -1) return null
  const next = items.slice()
  next[index] = {
    ...items[index],
    status: PHASE_STATUS[state.phase],
    total: state.total,
    done: state.dialed,
    failed: state.failed,
    progress_percent:
      state.total > 0 ? Math.round((state.dialed / state.total) * 100) : 0,
    started_at: state.started_at,
    completed_at: state.completed_at,
  }
  return next
}

/** Fold a live-state frame into both caches the pill reads: the job-detail
 *  entry it renders from, and the unified jobs list that drives its presence
 *  and collapsed counters. Unknown session → refetch the list instead. */
function foldLiveState(
  queryClient: QueryClient,
  state: MassCallLiveState,
): void {
  const detailKey = getGetJobApiV1SendMassJobsJobIdGetQueryKey(state.session_id)
  const cachedDetail = queryClient.getQueryData<JobDetailResponse>(detailKey)
  if (cachedDetail && cachedDetail.data !== state) {
    queryClient.setQueryData<JobDetailResponse>(detailKey, {
      ...cachedDetail,
      data: state,
    })
  }

  const listKey = activeMassJobsQueryKey()
  const cachedList = queryClient.getQueryData<MassJobsResponse>(listKey)
  if (cachedList) {
    const items = patchSummary(cachedList.data, state)
    if (items) {
      queryClient.setQueryData<MassJobsResponse>(listKey, {
        ...cachedList,
        data: items,
      })
      return
    }
  }
  void queryClient.invalidateQueries({ queryKey: listKey })
}

const DIAL_EVENTS = [
  WS_MESSAGE_TYPE.powerDialerUpdated,
  WS_MESSAGE_TYPE.powerDialerUpdatedSnake,
] as const

export function useDialProgressSync(): void {
  const queryClient = useQueryClient()

  const onUpdate = useCallback(
    (envelope: WsEnvelope<MassCallLiveState>) => {
      const state = envelope.data
      if (!state?.session_id) return
      foldLiveState(queryClient, state)
    },
    [queryClient],
  )

  useWsSubscriptions<MassCallLiveState>(DIAL_EVENTS, onUpdate)
}

/** How often to re-pull the live state while a dial session runs — the offline
 *  fallback that keeps the pill morphing (dialing ↔ on call, counters, done)
 *  even when no socket frame arrives. */
const DIAL_POLL_MS = 3_000

/** The dial session's live state, hydrated by REST, kept fresh by
 *  {@link useDialProgressSync}'s socket patches with a poll as fallback. */
export function useDialLiveState(
  jobId: string | undefined,
): MassCallLiveState | null {
  const queryClient = useQueryClient()
  const { data } = useGetJobApiV1SendMassJobsJobIdGet(jobId ?? '', {
    query: { enabled: Boolean(jobId), refetchInterval: DIAL_POLL_MS },
  })
  const live = asLiveState(data?.data)

  // Polled state must reach the jobs list too (presence + collapsed counters),
  // exactly like a socket frame would; no-ops when nothing changed.
  useEffect(() => {
    if (live) foldLiveState(queryClient, live)
  }, [live, queryClient])

  return live
}
